import React, { useMemo } from 'react';
import _ from 'lodash';
import { connect } from 'react-redux';
import { Impression } from '@nerdwallet/analytics/react/impression';
import {
  AnalyticsPageView,
  AnalyticsProvider,
} from '@nerdwallet/seo-analytics';
import lazy, { Hydrate } from '../../components/lazy/lazy-universal';
import Results from '../../components/currency/results/results';
import {
  MCResultsPanel,
  MCControlPanel,
  MORTGAGE_CALCULATOR,
  MORTGAGE,
} from '../../components/mortgage-calculator/shared';
import ToolLinkCards from '../../components/currency/tool-link-cards/tool-link-cards';
import type {
  MortgageCalculator,
  MortgageRates,
  MortgageValidations,
} from '../../components/mortgage-calculator/shared';
import toolLinkConfig from '../../components/mortgage-calculator/shared/tool-link-config';
import { useUrlRecordContext } from '../../components/url-record';
import SegmentImpression from '../../lib/analytics/segment-impression';

export type MCResultsProps = {
  experiments?: any;
  location?: Record<string, any>;
  history?: Record<string, any>;
  mortgageCalculator?: MortgageCalculator;
  mortgageRates?: MortgageRates;
  validations?: MortgageValidations;
  route?: {
    name: string;
  };
  impressions?: any;
};

// Lazy Load Components
const LenderCards = lazy(
  () =>
    import(
      /* webpackChunkName: 'lazy-lender-cards' */ '../../components/currency/lender-cards/lender-cards'
    ),
  { hydrate: Hydrate.WHEN_VISIBLE }
);

const RelatedArticles = lazy(
  () =>
    import(
      /* webpackChunkName: 'lazy-related-articles' */ '../../components/currency/related-articles/related-articles'
    ),
  { hydrate: Hydrate.WHEN_VISIBLE }
);

const Methodology = lazy(
  () =>
    import(
      /* webpackChunkName: 'lazy-methodology' */ '../../components/currency/methodology/methodology'
    ),
  { hydrate: Hydrate.WHEN_VISIBLE }
);

const HlmEntry = lazy(
  () =>
    import(
      /* webpackChunkName: 'lazy-hlm-entry' */ '@nerdwallet/mortgage-hlm-entry'
    ),
  { hydrate: Hydrate.WHEN_VISIBLE }
);

const ExpandingLinkList = lazy(
  () =>
    import(
      /* webpackChunkName: 'lazy-expanding-link-list' */ '../../components/currency/expanding-link-list/expanding-link-list'
    ),
  { hydrate: Hydrate.WHEN_VISIBLE }
);

const MCResults = (props: MCResultsProps): JSX.Element => {
  const {
    history,
    impressions,
    location,
    mortgageCalculator,
    validations,
    route,
  } = props;

  const { title, synopsis } = useUrlRecordContext();

  const stateListLinks = useMemo(() => {
    const stateList = mortgageCalculator?.stateList ?? [];
    return stateList.map(({ stateName, stateSlug }) => ({
      href: `/mortgages/mortgage-calculator/${stateSlug}`,
      text: `${stateName} mortgage calculator`,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(mortgageCalculator?.stateList)]);

  const data = {
    ...mortgageCalculator,
    validations,
    impressions,
  };

  return (
    <AnalyticsProvider>
      <AnalyticsPageView name="mortgages_calculator">
        <Impression
          eventType="page_impression"
          entityName={
            route?.name === 'default'
              ? 'mortgages_calculator_form'
              : 'mortgages_calculator'
          }
        >
          <SegmentImpression
            entityName="mortgage_calculator"
            formName="mortgage_calculator"
          >
            <Results
              {..._.assign({}, props, {
                className: 'mc-app',
                controlPanel: <MCControlPanel mortgageCalculator={MORTGAGE} />,
                data,
                heading: title,
                subcopy: synopsis,
                lenders: (
                  <LenderCards
                    trackingFormName="mc"
                    namedQuery="mortgages_offers_mc"
                    analyticsTrackingConfig={{
                      entityName: 'mortgages_calculator_offer',
                      sectionName: 'mortgages_calculator_offer',
                      sectionPosition: 1,
                    }}
                  />
                ),
                resultsPanel: (
                  <MCResultsPanel
                    // @ts-ignore - ignore error
                    location={location}
                    history={history}
                    trackingFormName="mc-results"
                    mortgageCalculator={MORTGAGE}
                  />
                ),
                methodology: (
                  <Methodology
                    trackingFormName="mc-methodology"
                    impressionTracking
                  />
                ),
                relatedArticles: (
                  <RelatedArticles
                    entityName={MORTGAGE_CALCULATOR.RELATED_ARTICLES.entityName}
                    sectionName={
                      MORTGAGE_CALCULATOR.RELATED_ARTICLES.sectionName
                    }
                    sectionPosition={
                      MORTGAGE_CALCULATOR.RELATED_ARTICLES.sectionPosition
                    }
                    articles={MORTGAGE_CALCULATOR.RELATED_ARTICLES.articles}
                  />
                ),
                toolLinkCards: (
                  <ToolLinkCards
                    header="Other NerdWallet Resources"
                    cards={toolLinkConfig.cards}
                    impressionTrackingName="mortgages_calculator_tool_card"
                    data={data}
                    calculatorType="mortgage_calculator"
                    analyticsConfig={{
                      sectionName: 'mortgages_calculator_tool_card',
                      sectionPosition: 6,
                    }}
                  />
                ),
                additionalContent: (
                  <ExpandingLinkList
                    heading="Mortgage calculator by state"
                    links={stateListLinks}
                    tracking={{
                      formName: 'seemore_statemc_open',
                      fieldName: 'statemc',
                    }}
                  />
                ),
              })}
            >
              <HlmEntry zipCode={mortgageCalculator?.inputs?.zipCode} />
              <div />
            </Results>
          </SegmentImpression>
        </Impression>
      </AnalyticsPageView>
    </AnalyticsProvider>
  );
};

const mapStateToProps = (state) => ({
  mortgageRates: state?.mortgageRates,
  mortgageCalculator: state?.mortgageCalculator,
  identity: state?.identity,
  validations: state?.validations,
  experiments: state?.experiments,
  impressions: state?.impressions,
});

export default connect(mapStateToProps)(MCResults);
